import _get from 'lodash/get';
import _round from 'lodash/round';

import { locale, removeNulls } from '..';
import {
    controlChildren,
    getDepthOfDirectedTreeNode,
    countDirectedTreeNodes,
} from '.';

const {
    enUS: { ENTITY_TYPE_MAP },
} = locale;

interface SummarizeDirectedTreeInput {
    directedTree: Maybe<ProDirectedTreeNode>;
    config: SummarizeDirectedTreeConfig;
}

/**
 * Summarizes a directed tree and adds .href properties with image URLs for icons.
 * @param {SummarizeDirectedTreeInput} input
 * @returns {ProDirectedTreeNode[] | null}
 */
export const summarizeDirectedTree = ({
    directedTree,
    config,
}: SummarizeDirectedTreeInput): DirectedTreeNodeWithHref[] | null => {
    if (directedTree === null) {
        return null;
    }

    const {
        maxNodeCount,
        minTotalTrackEarnings,
        minNodeEarnings,
        defaultEntityType,
        iconMap,
    } = config;

    // Fall back to trackIcon if entityType is not found in entityTypeToIconMap
    const getIconHref = (entityType: string): string =>
        _get(iconMap, [entityType], iconMap.track);

    // Filter out any nodes with earnings that round to less than the minNodeEarnings
    const reducerFilter = (
        acc: ProDirectedTreeNode[],
        node: ProDirectedTreeNode,
    ): ProDirectedTreeNode[] => {
        // Leave out the node if:
        // - it is null
        // - it has a non-null `earnings` that is less than the minimum
        if (
            node === null ||
            (node.earnings !== null &&
                node.earnings !== undefined &&
                _round(node.earnings, 2) < minNodeEarnings)
        ) {
            return acc;
        }

        // If the node contains children, run the filter on them as well.
        const newChildren = Array.isArray(node.children)
            ? removeNulls(node.children).reduce(reducerFilter, [])
            : null;

        return acc.concat([
            {
                ...node,
                children: newChildren,
            },
        ]);
    };

    /**
     * Augment nodes with `href` properties and summarize data.
     */
    const mapper = (node: ProDirectedTreeNode): DirectedTreeNodeWithHref => {
        const href = getIconHref(node?.entity_type ?? '__none__');

        // If there are no children, add the href and set children to empty array.
        // Intermediate nodes with children === null will break the math that amCharts uses to add
        // up the totals.
        if (node.children === null || node.children === undefined) {
            return {
                ...node,
                children: [],
                href,
            };
        }

        // If the node is not the parent of a leaf node, recurse
        if (getDepthOfDirectedTreeNode(node) > 1) {
            return {
                ...node,
                href,
                children: removeNulls(node.children).map(mapper),
            };
        }

        // If we have made it this far, the node is a parent of a leaf node and its children
        // are tracks.

        // If total earnings of children (tracks) is less than the minimum, summarize the
        // children into a single node
        const totalTrackEarnings = removeNulls(node.children).reduce(
            (acc, childNode: ProDirectedTreeNode): number => {
                if (childNode.earnings !== null) {
                    return acc + (childNode?.earnings ?? 0);
                }

                return acc;
            },
            0,
        );

        if (totalTrackEarnings < minTotalTrackEarnings) {
            const entityTypeOfFirstChild = _get(
                node,
                ['children', '0', 'entity_type'],
                defaultEntityType,
            );
            const entityTypeDisplayName = _get(
                ENTITY_TYPE_MAP,
                [entityTypeOfFirstChild],
                defaultEntityType,
            );
            const childEntityTypeSingularOrPlural =
                node.children.length === 1
                    ? entityTypeDisplayName
                    : `${entityTypeDisplayName}s`;

            return {
                ...node,
                earnings: totalTrackEarnings,
                href,
                entity_name: `${node.entity_name} (${node.children.length} ${childEntityTypeSingularOrPlural})`,
                children: null,
            };
        }

        // If the totalTrackEarnings are high enough to include the children, limit their number
        // with controlChildren()
        const controlledChildrenWithIcons = controlChildren({
            children: removeNulls(node.children),
            maxChildren: 25,
            sortKey: 'earnings',
            trackIcon: iconMap.track,
        }).map(
            (trackNode: ProDirectedTreeNode): DirectedTreeNodeWithHref => ({
                ...trackNode,
                href: getIconHref(trackNode?.entity_type ?? '__none__'),
            }),
        );

        return {
            ...node,
            href,
            children: controlledChildrenWithIcons,
        };
    };

    const filteredData = [directedTree].reduce(reducerFilter, []);
    const nodeList = filteredData.map(mapper);
    const nodeCount = nodeList.reduce(
        (acc: number, node: ProDirectedTreeNode): number =>
            acc + countDirectedTreeNodes(node),
        0,
    );

    if (nodeCount > maxNodeCount) {
        return null;
    }

    return nodeList;
};
